import { StandOperationResult } from '@/ble/entities/standOperationResult'
import { SwitchStatus } from '@/ble/entities/switchStatus'
import { readTimesOf } from '@/ble/entities/operationStatus'
import { TagId } from '@/ble/entities/tagId'

// /service/v2/ble/state に送る当事者情報。processId/appVersion は呼び出し側が用意する。
export type BleStateContext = {
  ikasaId: string
  standId: string
  processId: string
  appVersion: string
  when: 'rental' | 'return'
  forcedUnlock: boolean // = capabilities.canDistinguishReaders
}

// backend DTO(kasa_in_out_histories)に一致する snake_case ペイロード。
// strict binding(DisallowUnknownFields)のため DTO 定義キーだけを出す。
// wrote と pre_closed/closed を判別 union で分け、キーの typo・送り忘れ・余剰キーを
// コンパイル時にも弾く(実行時の厳密キー集合テストと二重防御)。
type BleStateBaseKeys = {
  ikasa_id: string
  stand_id: string
  process_id: string
  app_version: string
  when: 'rental' | 'return'
  forced_unlock: boolean
}

export type BleStateWrotePayload = BleStateBaseKeys & { state: 'wrote' }

export type BleStateResultPayload = BleStateBaseKeys & {
  state: 'pre_closed' | 'closed'
  read_times: number
  tag_id: string
  second_tag_id: string
  is_pressed: boolean
  switch_state: 'first' | 'second' | null // backend vo.NewSwitchState が受理する値のみ
  firm_version?: string // 応答に firmwareVersion が無い世代では送らない
}

export type BleStateForcedClosedPayload = BleStateBaseKeys & {
  state: 'forced_closed'
}

export type BleStatePayload =
  | BleStateWrotePayload
  | BleStateResultPayload
  | BleStateForcedClosedPayload

// 書込直前(結果未確定)のペイロード。結果由来フィールドは含めない。
export function buildWrotePayload(ctx: BleStateContext): BleStateWrotePayload {
  return {
    ikasa_id: ctx.ikasaId,
    stand_id: ctx.standId,
    process_id: ctx.processId,
    app_version: ctx.appVersion,
    state: 'wrote',
    when: ctx.when,
    forced_unlock: ctx.forcedUnlock,
  }
}

// 結果確定後(pre_closed / closed)のペイロード。
// platform onFetchedStandOperationResult + recordBleStatePayload の移植。
export function buildResultPayload(
  ctx: BleStateContext,
  result: StandOperationResult
): BleStateResultPayload {
  const isPressed = judgeIsPressed(ctx.when, result.switchStatus)
  const payload: BleStateResultPayload = {
    ...buildWrotePayload(ctx),
    state: result.isIntermediateRead ? 'pre_closed' : 'closed',
    read_times: readTimesOf(result.outcome),
    // /ble/state の tag_id は area1TagId(per-reader raw)。selectedTagId ではない。
    tag_id: result.area1TagId?.id ?? TagId.irregular,
    second_tag_id: result.area2TagId?.id ?? TagId.irregular,
    is_pressed: isPressed,
    // backend vo.NewSwitchState(vo/switch_state.go)は 'first'/'second' のみ受理するため
    // DTO 向けに操作方向で変換する。switch_state は isPressed=true のときのみ非null で、
    // isPressed は操作方向スイッチ押下(rental=pressedFirst/Both・return=pressedSecond/Both)
    // のときのみ true なので、pressedBoth 含め操作方向で一意に畳める。
    // 注意: ここは platform アプリと送信値が異なる。platform は enum の文字列
    // ('SwitchStatus.pressedFirst' 形式)を送っていて backend で '' に落ちるため、
    // 挙動を platform に揃える対象にしない（正は backend VO が受理する値）。
    switch_state: isPressed
      ? ctx.when === 'rental'
        ? 'first'
        : 'second'
      : null,
  }
  // firmwareVersion を humps 素通しすると firmware_version(DTO外)→拒否(strict binding。
  // adapter fallback で実際は500)。明示 firm_version で回避。
  if (result.firmwareVersion != null) {
    payload.firm_version = result.firmwareVersion
  }
  return payload
}

// 手動完了(ユーザー申告)の記録。結果フレーム由来のキーは存在しないので送らない。
// backend DTO は必須3キー以外 omitempty であり、native が送る tag_id=''/read_times=null と
// DB 到達値は同一(adapter Get* が zero 値に畳む)。
export function buildForcedClosedPayload(
  ctx: BleStateContext
): BleStateForcedClosedPayload {
  return { ...buildWrotePayload(ctx), state: 'forced_closed' }
}

// platform _judgeIsPressed(report_bluetooth_status_data.dart)と同一。aesStandData.ts の
// isStandSwitchPressed と同ロジックだが、platform も reporter 側に別実装を持つ二重構造の
// 忠実な写しとしてここに新設している（統合はしない）。操作方向のみ true。
export function judgeIsPressed(
  when: 'rental' | 'return',
  switchStatus: SwitchStatus
): boolean {
  if (when === 'rental') {
    return switchStatus === 'pressedFirst' || switchStatus === 'pressedBoth'
  }
  return switchStatus === 'pressedSecond' || switchStatus === 'pressedBoth'
}
